import React from 'react';
import { FormatNumber2 } from '../../functions/formatNumber';

const getColumnWidth = (column) => {
  if (column.width) return Number(column.width);
  if (column.flex) return Number(column.flex) * 180;
  return 120;
};

const useWindowWidth = () => {
  const getWidth = () =>
    typeof window === 'undefined' ? 1440 : window.innerWidth;
  const [width, setWidth] = React.useState(getWidth);

  React.useEffect(() => {
    const handleResize = () => setWidth(getWidth());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
};

const getCellValue = (column, row) => {
  const value = row[column.field];
  const tableRow = { ...row, original: row };

  if (column.renderCell) {
    return column.renderCell({ value, row: tableRow });
  }

  if (column.valueFormatter) {
    return column.valueFormatter({ value, row: tableRow });
  }

  return value ?? '';
};

const getHeaderValue = (column) => {
  if (column.renderHeader) {
    return column.renderHeader({ field: column.field });
  }

  return column.headerName || column.label || column.field;
};

const getColumnText = (column) =>
  `${column.field || ''} ${column.headerName || ''} ${column.label || ''}`.toLowerCase();

const isSummableColumn = (column) => {
  if (column.sum === true) return true;
  if (column.sum === false) return false;

  const text = getColumnText(column);
  const nonTotalWords = [
    'date',
    'day',
    'id',
    'sid',
    'no',
    'number',
    'invoice',
    'inv',
    'voucher',
    'mobile',
    'phone',
    'name',
    'address',
    'city',
    'station',
    'party',
    'item',
    'description',
    'approved',
    'verified',
    'status',
    'code',
    'hsn',
    'gst %',
    'percent',
    '%',
    'price',
    'rate',
  ];
  const totalWords = [
    'amount',
    'amt',
    'qnty',
    'qty',
    'quantity',
    'debit',
    'credit',
    'balance',
    'cash',
    'collection',
    'due',
    'expense',
    'less',
    'profit',
    'paid',
    'received',
    'return',
    'issue',
    'sale',
    'total',
    'btc',
    'card',
    'cheque',
    'epayment',
    'discount',
    'disq',
  ];

  if (!totalWords.some((word) => text.includes(word))) return false;
  return !nonTotalWords.some((word) => text.includes(word));
};

const parseNumericValue = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value !== 'string') return 0;

  const cleanedValue = value.replace(/,/g, '').replace(/[^\d.-]/g, '').trim();
  if (!cleanedValue || cleanedValue === '-' || cleanedValue === '.') return 0;

  const parsedValue = Number(cleanedValue);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const hasAnyWord = (text, words) => words.some((word) => text.includes(word));

const isIdentityColumn = (column) =>
  hasAnyWord(getColumnText(column), [
    'date',
    'item name',
    'party name',
    'station name',
    'name',
    'description',
  ]);

const getResponsivePriority = (column) => {
  if (Number.isFinite(Number(column.responsivePriority))) {
    return Number(column.responsivePriority);
  }
  if (column.hideOnSmall) return 5;
  if (column.keepOnSmall || column.mobileVisible) return 1;

  const text = getColumnText(column);

  if (
    hasAnyWord(text, [
      'amount',
      'amt',
      'balance',
      'debit',
      'credit',
      'profit',
      'qty',
      'qnty',
      'quantity',
      'collection',
      'cash',
      'total',
      'sale',
      'issue',
      'return',
      'paid',
      'received',
      'due',
    ])
  ) {
    return 1;
  }

  if (isIdentityColumn(column)) return 1;

  if (
    hasAnyWord(text, [
      'invoice',
      'inv',
      'voucher',
      'v no',
      'vno',
      'bill',
      'code',
      'sid',
      'acc',
      'account',
      'mobile',
    ])
  ) {
    return 2;
  }

  if (
    hasAnyWord(text, [
      'address',
      'city',
      'hsn',
      'gst',
      'cgst',
      'sgst',
      'igst',
      '%',
      'approved',
      'verified',
      'status',
    ])
  ) {
    return 4;
  }

  return 3;
};

const getResponsiveWidth = (column, windowWidth) => {
  const width = getColumnWidth(column);
  const text = getColumnText(column);
  const isMobile = windowWidth <= 640;
  const isTablet = windowWidth <= 900;

  if (!isTablet) return width;
  if (column.mobileWidth && isMobile) return Number(column.mobileWidth);
  if (column.tabletWidth && isTablet) return Number(column.tabletWidth);

  if (hasAnyWord(text, ['item name', 'party name', 'station name', 'description', 'address'])) {
    return Math.min(width, isMobile ? 150 : 190);
  }

  if (hasAnyWord(text, ['date'])) return Math.min(width, isMobile ? 92 : 105);
  if (hasAnyWord(text, ['invoice', 'voucher', 'v no', 'vno', 'code', 'sid', 'acc'])) {
    return Math.min(width, isMobile ? 72 : 88);
  }
  if (hasAnyWord(text, ['amount', 'amt', 'balance', 'debit', 'credit', 'profit', 'cash', 'total'])) {
    return Math.min(width, isMobile ? 104 : 120);
  }
  if (hasAnyWord(text, ['qty', 'qnty', 'quantity'])) return Math.min(width, isMobile ? 78 : 92);
  if (hasAnyWord(text, ['mobile', 'phone'])) return Math.min(width, isMobile ? 92 : 105);

  return Math.max(64, Math.min(width, Math.round(width * (isMobile ? 0.68 : 0.82))));
};

const getResponsiveColumns = (columns, windowWidth) => {
  if (windowWidth > 900) return columns;

  const maxColumns = windowWidth <= 430 ? 5 : windowWidth <= 640 ? 6 : 9;
  const allowedPriority = windowWidth <= 430 ? 1 : windowWidth <= 640 ? 2 : 3;
  const essentialIndexes = new Set();
  const numericIndexes = [];

  columns.forEach((column, index) => {
    if (isIdentityColumn(column) && essentialIndexes.size < 2) {
      essentialIndexes.add(index);
    }
    if (isSummableColumn(column)) {
      numericIndexes.push(index);
    }
  });

  numericIndexes.slice(0, Math.max(2, maxColumns - essentialIndexes.size)).forEach((index) => {
    essentialIndexes.add(index);
  });

  const visibleColumns = columns.filter((column, index) => {
    if (column.hideOnMobile && windowWidth <= 640) return false;
    if (column.hideOnTablet && windowWidth <= 900) return false;
    if (essentialIndexes.has(index)) return true;
    return getResponsivePriority(column) <= allowedPriority;
  });

  const cappedColumns =
    visibleColumns.length > maxColumns
      ? columns.filter((column, index) => {
          const selectedIndexes = new Set(essentialIndexes);

          columns
            .map((tableColumn, columnIndex) => ({
              columnIndex,
              priority: getResponsivePriority(tableColumn),
            }))
            .filter(
              ({ columnIndex, priority }) =>
                priority <= allowedPriority &&
                !selectedIndexes.has(columnIndex) &&
                visibleColumns.includes(columns[columnIndex])
            )
            .sort((a, b) => a.priority - b.priority || a.columnIndex - b.columnIndex)
            .slice(0, Math.max(0, maxColumns - selectedIndexes.size))
            .forEach(({ columnIndex }) => selectedIndexes.add(columnIndex));

          return selectedIndexes.has(index) && visibleColumns.includes(column);
        })
      : visibleColumns;

  return cappedColumns.map((column) => ({
    ...column,
    width: getResponsiveWidth(column, windowWidth),
  }));
};

const ReportDataTable = ({
  columns,
  rows,
  loading = false,
  getRowKey,
  onRowDoubleClick,
  getRowClassName,
  emptyTitle = 'No records found',
  emptyDescription = 'Run the report to view data.',
  footerValue,
  minWidth,
  maxHeight = 520,
  showAutoFooter = true,
  responsive = true,
}) => {
  const windowWidth = useWindowWidth();
  const visibleColumns = React.useMemo(
    () => (responsive ? getResponsiveColumns(columns, windowWidth) : columns),
    [columns, responsive, windowWidth]
  );
  const tableMinWidth =
    minWidth || visibleColumns.reduce((total, column) => total + getColumnWidth(column), 0);
  const isFullHeight = maxHeight === '100%';
  const autoTotals = React.useMemo(() => {
    if (!showAutoFooter || footerValue || loading || !rows.length) return null;

    const totals = visibleColumns.reduce((result, column) => {
      if (!isSummableColumn(column)) return result;

      const value = rows.reduce(
        (sum, row) => sum + parseNumericValue(row[column.field]),
        0
      );

      if (value !== 0) {
        result[column.field] = value;
      }

      return result;
    }, {});

    return Object.keys(totals).length ? totals : null;
  }, [visibleColumns, footerValue, loading, rows, showAutoFooter]);
  const tableFooterValue = footerValue
    ? footerValue
    : autoTotals
    ? (column) => {
        if (Object.prototype.hasOwnProperty.call(autoTotals, column.field)) {
          return FormatNumber2(autoTotals[column.field]);
        }

        const firstColumnWithText = visibleColumns.find(
          (tableColumn) => !isSummableColumn(tableColumn)
        );
        return firstColumnWithText?.field === column.field ? 'TOTAL' : '';
      }
    : null;

  return (
    <div
      className={`report-data-table overflow-hidden rounded-md border border-slate-300 bg-white shadow-sm ${
        isFullHeight ? 'flex h-full min-h-0 flex-col' : ''
      }`}
    >
      <div
        className={`overflow-auto pb-3 ${isFullHeight ? 'min-h-0 flex-1' : ''}`}
        style={{ maxHeight }}
      >
        <table
          className="w-full table-fixed border-collapse text-xs"
          style={{ minWidth: tableMinWidth }}
        >
          <colgroup>
            {visibleColumns.map((column) => (
              <col key={column.field} style={{ width: getColumnWidth(column) }} />
            ))}
          </colgroup>
          <thead className="report-data-table-head bg-slate-100 text-slate-700">
            <tr>
              {visibleColumns.map((column) => (
                <th
                  key={column.field}
                  className="report-data-table-header sticky top-0 z-[1] border-b border-r border-slate-300 bg-slate-100 px-2 py-2 text-center font-bold text-slate-700 last:border-r-0"
                >
                  <div className="flex min-h-[20px] items-center justify-center text-center">
                    {getHeaderValue(column)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 10 }).map((_, rowIndex) => (
                <tr key={`loading-${rowIndex}`} className="odd:bg-white even:bg-slate-50/70">
                  {visibleColumns.map((column) => (
                    <td
                      key={column.field}
                      className="h-9 border-b border-r border-slate-200 px-2 last:border-r-0"
                    >
                      <div className="h-3.5 animate-pulse rounded bg-slate-200" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length ? (
              rows.map((row, index) => (
                <tr
                  key={getRowKey ? getRowKey(row, index) : row.id || index}
                  onDoubleClick={() => onRowDoubleClick?.(row)}
                  className={`odd:bg-white even:bg-slate-50/70 hover:bg-blue-50 ${
                    getRowClassName ? getRowClassName(row, index) : ''
                  }`}
                >
                  {visibleColumns.map((column) => {
                    const value = getCellValue(column, row);
                    return (
                      <td
                        key={column.field}
                        title={typeof value === 'string' || typeof value === 'number' ? String(value) : ''}
                        className={`h-9 truncate border-b border-r border-slate-200 px-2 last:border-r-0 ${
                          column.align === 'right'
                            ? 'text-right tabular-nums'
                            : column.align === 'center'
                            ? 'text-center'
                            : 'text-left'
                        }`}
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={visibleColumns.length} className="h-48 px-6 text-center text-slate-500">
                  <div className="font-semibold text-slate-700">{emptyTitle}</div>
                  <div className="mt-1 text-sm">{emptyDescription}</div>
                </td>
              </tr>
            )}
          </tbody>
          {!loading && rows.length > 0 && tableFooterValue ? (
            <tfoot className="bg-slate-200 text-slate-900">
              <tr>
                {visibleColumns.map((column) => (
                  <td
                    key={column.field}
                    className={`border-r border-t border-slate-300 px-2 py-2 font-bold last:border-r-0 ${
                      column.align === 'right'
                        ? 'text-right tabular-nums'
                        : column.align === 'center'
                        ? 'text-center'
                        : 'text-left'
                  }`}
                >
                    {tableFooterValue(column)}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
};

export default ReportDataTable;
